/**
 * Renders the "datepicker" kind via createDatepickerFieldController — a
 * trigger button that opens a real calendar grid popup (prev/next month,
 * arrow-key navigation via the shared calendarKeyboardTarget the
 * controller already wires up).
 */
import { observerFor } from "@modyra/core";
import { buildDateLocale, formatIsoDate, formatLocalizedDate, parseLocalizedDate, parseIsoDate } from "@modyra/core/datetime";
import { fieldAccessibleName, keyMeans, applySubmissionNames, syncSubmitValues, MDY_WIDGET_CONTRACTS, createDatepickerFieldController, overlayAnchoringFor, visibleErrorsOf, partClasses, calendarViewOnToggle, MDY_I18N_MESSAGES_DEFAULT, keyBindingFor, presentationClass, dateEntryText, datepickerFieldPartIds } from "@modyra/widgets";
import { applyPart, el, setErrors, setText, setIcon } from "../dom.js";
import { buildFieldShell, insertControl } from "../field-shell.js";
import { withControls } from "../field-controls.js";
import { bindViewZoom, buildCalendarGrid, buildPeriodViews, fillCalendar } from "./calendar.js";
import { runCommands } from "../command-runtime.js";
import { dismissOnFocusOutside } from "../overlay.js";
import { dismissOnOutsidePointer, positionOverlay, releaseOverlayPlacement, reflectOverlayOpen, trackOverlay } from "../overlay.js";
/**
 * The class every part and box wears, asked of the contract once.
 *
 * Spelled here too, each of these is a second copy of a name the catalogue holds — and the copy is
 * where the two come to disagree without either moving.
 */
const CLASS = {
    control: partClasses("datepicker", "control").join(" "),
    dialogHeader: partClasses("datepicker", "dialogHeader").join(" "),
    toggle: partClasses("datepicker", "toggle").join(" "),
    box: presentationClass("datepicker", "box"),
    viewToggle: presentationClass("datepicker", "viewToggle"),
    title: presentationClass("datepicker", "title"),
    navButton: presentationClass("datepicker", "navButton"),
};
export function renderDatepickerField(container, f, handle, reactivity, options = {}, widgetId = f.name, 
/**
 * The words this control shows. The engine has no opinion about them, so they arrive from the
 * widget contract's tables rather than being written here — three renderers each spelling
 * "open the calendar" is three answers to one question.
 */
messages = MDY_I18N_MESSAGES_DEFAULT) {
    reactivity = observerFor(handle, reactivity);
    // How this popup attaches is the contract's, not this renderer's.
    const anchoring = overlayAnchoringFor("datepicker");
    // Month and weekday names, and the first day of the week, come from Intl via `buildDateLocale`.
    // Read before the controller is built, because the controller reads a typed entry through it.
    const dateLocale = buildDateLocale(options.locale ?? (typeof navigator === "undefined" ? "en-US" : navigator.language), options.firstDayOfWeek);
    const controller = createDatepickerFieldController({
        supportingText: () => f.supportingText ?? null,
        widgetId: widgetId,
        handle,
        ...options,
        // The reading is locale-aware, so it belongs to the renderer; the *judgement* — commit, clear or
        // keep — belongs to the controller, which is what stops two renderers answering differently.
        parseEntry: (text) => {
            const parsed = parseLocalizedDate(text, dateLocale.locale);
            return parsed ? formatIsoDate(parsed) : null;
        },
    }, reactivity);
    const shell = buildFieldShell(f.label, "datepicker", {}, f.ariaLabel, f.name, f.supportingText);
    // The catalogue's datepicker anatomy: a typeable input plus a toggle button that opens the
    // calendar, rather than one button doing both jobs.
    const control = el("input", CLASS.control);
    control.type = "text";
    if (f.placeholder)
        control.placeholder = f.placeholder;
    const toggle = el("button", CLASS.toggle);
    setIcon(toggle, "CALENDAR");
    toggle.type = "button";
    toggle.setAttribute("aria-label", messages.datepickerToggleLabel);
    // The popup, its header and the day cells carry the class names the shipped themes already
    // style (`mdy-datepicker__popup` positions and frames the panel, `__header` lays out the
    // month nav) — the controller only names the trigger and the grid.
    const popup = el("div", `${MDY_WIDGET_CONTRACTS.datepicker.parts.popup.classes.join(" ")} mdy-overlay`);
    const header = el("div", CLASS.dialogHeader);
    const prevButton = el("button", `${CLASS.navButton} ${CLASS.navButton}--previous`);
    prevButton.type = "button";
    setIcon(prevButton, "CHEVRON_LEFT");
    prevButton.setAttribute("aria-label", messages.datepickerPreviousMonth);
    // The header label opens the month view, which opens the year view: paging a month at a time put
    // a birth date thirty clicks away, and the other two renderers had grown this and this one had
    // not. Nobody decided that — see the calendar view contract.
    const monthLabel = el("button", CLASS.viewToggle);
    monthLabel.type = "button";
    // The month as its own element inside the button. The grid is named by referring to it, and the
    // button carries a name of its own — "change view, currently …" — so a reference crossing into the
    // button would announce the way out instead of where you are. ADR 0212.
    const monthTitle = el("span", CLASS.title);
    monthTitle.id = datepickerFieldPartIds(widgetId).headingId;
    monthLabel.append(monthTitle);
    const nextButton = el("button", `${CLASS.navButton} ${CLASS.navButton}--next`);
    nextButton.type = "button";
    setIcon(nextButton, "CHEVRON_RIGHT");
    nextButton.setAttribute("aria-label", messages.datepickerNextMonth);
    header.append(prevButton, monthLabel, nextButton);
    const grid = buildCalendarGrid("datepicker");
    const bounds = () => ({ min: parseIsoDate(options.minDate ?? null), max: parseIsoDate(options.maxDate ?? null) });
    const { monthPicker, yearPicker, render: renderPeriodView } = buildPeriodViews("datepicker", widgetId, dateLocale, bounds, dispatch);
    // The calendar frames the month inside the popup. The popup positions and the calendar lays out:
    // the themes give this element the column flow the header and grid sit in, so a popup holding them
    // directly is a picker the shipped themes cannot arrange.
    const calendar = el("div", MDY_WIDGET_CONTRACTS.datepicker.parts.calendar.classes.join(" "));
    // What the opener promises, carried by the thing it opens. The other two renderers wrote the role
    // here and this one wrote it nowhere, so a person was told a dialog had appeared and met an
    // unnamed `<div>`. Read from the catalogue rather than spelled again.
    applyPart(calendar, MDY_WIDGET_CONTRACTS.datepicker.parts.calendar);
    // A dialog owes a name: announced as "dialog" and nothing else, a person is told a panel has
    // appeared and not what it is for. The field's own words where there are any, the kind's sentence
    // otherwise — the same order the other two renderers use.
    calendar.setAttribute("aria-label", fieldAccessibleName({ label: f.label, name: f.name }) || messages.datepickerChooseDate);
    calendar.append(header, grid, monthPicker, yearPicker);
    popup.append(calendar);
    const wrapper = el("div", CLASS.box);
    wrapper.append(control, toggle, popup);
    insertControl(shell, wrapper);
    container.appendChild(shell.root);
    let cellEls = new Map();
    let renderedYear = null;
    let renderedMonth = null;
    /** Whether the last render showed an open calendar's days, so the render that brings them back is told apart. */
    let showedTheDays = false;
    const lookup = (part) => (part === "trigger" ? control : undefined);
    function dispatch(intent) {
        const commands = controller.dispatch(intent);
        runCommands(commands, lookup, {
            setOpen: () => undefined,
            onTouched: () => handle.markAsTouched(),
            onDirty: () => handle.markAsDirty(),
        });
    }
    // Focus is not a safe "the user is editing" signal here: committing a date restores focus to the
    // input, so a focus-guarded sync would skip the very update that matters. Typing is the signal.
    let typing = false;
    const toggleOverlay = () => dispatch(controller.state().open ? { type: "close", restoreFocus: false } : { type: "open" });
    toggle.addEventListener("click", toggleOverlay);
    // The control opens the overlay and never closes it: it is the field the user types into, so a
    // click there is the caret being placed, not a switch being flipped. The toggle button is the
    // switch. `MDY_POPUP_OPENERS[kind].typeable` is where the contract says so.
    control.addEventListener("click", () => { if (!controller.state().open)
        dispatch({ type: "open" }); });
    // And from the keyboard, on whichever keys the contract says open this kind. Asked of the table
    // rather than listed here: a popup only a pointer can reach is closed to anyone who does not use
    // one, and a renderer choosing its own keys is how the three of them come to answer differently.
    control.addEventListener("keydown", (event) => {
        const open = controller.state().open;
        if (open || !keyMeans("datepicker", event, "open", open))
            return;
        event.preventDefault();
        dispatch({ type: "open" });
    });
    control.addEventListener("input", () => { typing = true; });
    control.addEventListener("blur", () => { typing = false; dispatch({ type: "blur" }); });
    // The text goes to the controller as text. Parsing here and dispatching only on success is what
    // made an unreadable entry vanish: nothing was dispatched, and the sync below then rewrote the
    // input from a value that had not changed.
    control.addEventListener("change", () => {
        typing = false;
        dispatch({ type: "type", text: control.value });
    });
    prevButton.addEventListener("click", () => dispatch({ type: "navigate-month", delta: -1 }));
    nextButton.addEventListener("click", () => dispatch({ type: "navigate-month", delta: 1 }));
    grid.addEventListener("keydown", (event) => {
        if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", "Enter", " ", "Escape"].includes(event.key)) {
            event.preventDefault();
            dispatch({ type: "keydown", key: event.key, shiftKey: event.shiftKey, ctrlKey: event.ctrlKey, metaKey: event.metaKey });
        }
    });
    // Escape dismisses from wherever the user is. The grid handles it too, as one of the keys the
    // calendar navigates with, but this overlay does not take focus when it opens — so a user who
    // opened it from the toggle and pressed Escape was holding a dialog that answered nothing. It is
    // the one key an overlay must always answer, which is what makes reaching it from the toggle part
    // of the behaviour rather than a convenience.
    const onEscape = (event) => {
        if (!controller.state().open)
            return;
        // Asked of the catalogue, not named here. The binding declares that a dismissal answers whatever
        // is held with it, and a condition naming the key is a second copy of that rule — the copy is
        // what keeps answering after the declaration changes, which is how this stayed correct while
        // being correct for its own reasons. ADR 0168.
        // The binding, not the intent. Both dismissals are declared `cancel` and they differ in where
        // focus lands — `restoresFocus` is the field that says which — so asking only for the intent
        // answers `Tab` with `Escape`'s rule and hands the keyboard back to the control it was leaving.
        // The phase is asked rather than assumed: a shut control asked about the open phase answers with
        // the bindings of a panel that is not there. ADR 0168.
        const binding = keyBindingFor("datepicker", event, controller.state().open);
        if (binding?.intent !== "cancel")
            return;
        dispatch({ type: "close", restoreFocus: binding.restoresFocus === true });
    };
    /**
     * Stepping out to a wider view and back in, from wherever in the calendar the person is standing.
     *
     * Answered on the popup rather than on the grid, because two of the three views are not the grid:
     * a handler bound there would work while the days are showing and stop once it had done its job
     * once. Which view a step reaches is `calendarViewOnZoom`'s answer, not this renderer's.
     *
     * The gesture is asked of the catalogue. A condition naming the key here would also have to name
     * the modifier, and it is the modifier that separates this from the bare arrow that walks the
     * grid — a copy of that distinction is one that keeps answering after the declaration moves.
     */
    // The popup sits inside the wrapper *and* may be moved out of it, so both carry this listener and
    // one press can reach it twice. A dismissal survives that — closing a closed panel is closing it —
    // but a step does not: two runs of one press walked the days to the months and straight on to the
    // years, which reads as a key that skips a view rather than as a handler that ran twice.
    // The other views, reached from the keyboard by the gesture the kind declares.
    bindViewZoom("datepicker", [wrapper, popup], { days: grid, months: monthPicker, years: yearPicker }, () => controller.state(), (mode) => dispatch({ type: "set-view-mode", mode }));
    // After the views: from a month or a year, Escape is theirs and goes back to the days.
    wrapper.addEventListener("keydown", onEscape);
    popup.addEventListener("keydown", onEscape);
    // The header goes to the top of the funnel and choosing narrows back down. Which view it opens
    // is `calendarViewOnToggle`'s answer, not this renderer's: writing it here is how three renderers
    // came to disagree about what the same control does.
    monthLabel.addEventListener("click", () => dispatch({ type: "set-view-mode", mode: calendarViewOnToggle(controller.state().viewMode) }));
    const undismiss = dismissOnOutsidePointer([wrapper], () => controller.state().open, () => dispatch({ type: "close", restoreFocus: false }));
    // The other half of how this kind says it is dismissed. A calendar left open behind a field
    // somebody has tabbed away from covers the next question and answers to a keyboard that has gone.
    const unfocusout = dismissOnFocusOutside("datepicker", [wrapper, shell.root, popup], () => controller.state().open, () => dispatch({ type: "close", restoreFocus: false }), { pointer: undismiss, markVisited: () => handle.markAsTouched() });
    const untrack = trackOverlay(popup, shell.wrapper, () => controller.state().open, anchoring);
    const effectRef = reactivity.effect(() => {
        const state = controller.state();
        const view = controller.view();
        applyPart(shell.label, view.parts.label);
        applyPart(control, view.parts.trigger);
        toggle.disabled = state.disabled;
        applyPart(grid, view.parts.grid);
        applyPart(shell.description, view.parts.description);
        applyPart(shell.errorList, view.parts.error);
        // An entry the field could not read is a verdict of this control's own: the form holds nothing,
        // so it has no error to give, and saying nothing would leave the person looking at their own
        // text believing it was taken.
        // Said to the form as well as to the page: the field holds a value its own rules accept — `null`,
        // which nothing objects to — while the person is looking at text this control could not read, so
        // without this the submit went out holding nothing where they had typed something.
        // Reported to the form first, so the entry is one of the field's errors like any other — and read
        // back through `visibleErrorsOf`, which is where "out of play, no verdict" lives. Painting from
        // `entryUnreadable` directly kept announcing a control nobody could touch.
        handle.reportEntry(state.entryUnreadable ? messages.entryUnreadable : null);
        setErrors(shell.errorList, visibleErrorsOf(handle).map((e) => e.message));
        // The same question the projection already answered, asked the same way. Asked as *is this field
        // invalid* it is true from the moment a required field is drawn empty, so a control announced a
        // refusal about a rule nobody had been given a turn at — and the renderer's own write landed on
        // top of the contract's, which had said the opposite correctly. Two answers to one question, and
        // the later one wins.
        control.setAttribute("aria-invalid", String(visibleErrorsOf(handle, "datepicker").length > 0));
        shell.syncState({
            open: state.open,
            touched: handle.touched(), disabled: handle.disabled(), readonly: handle.readonly(),
            hasError: visibleErrorsOf(handle).length > 0, filled: Boolean(state.selectedDate), required: handle.required(), constraints: handle.constraints?.() ?? null,
        });
        // The input mirrors the committed value, except while the person is typing — and except while it
        // holds an entry the field could not read, which stays where they can correct it.
        const display = dateEntryText(state.entryText, formatLocalizedDate(state.selectedDate, dateLocale.locale));
        if (!typing && control.value !== display)
            control.value = display;
        reflectOverlayOpen(popup, state.open, messages);
        if (!state.open)
            releaseOverlayPlacement(popup);
        // The same words in every view, which is what the other renderers of this contract show.
        //
        // A header that reads `2026` while the years are on screen is a button whose text is identical
        // to one of the cells under it: anything looking for the year — a person, a test, a tool — finds
        // the header first and presses the way back instead of the year they meant.
        setText(monthTitle, `${dateLocale.monthNamesLong[state.viewMonth - 1]} ${state.viewYear}`);
        monthLabel.setAttribute("aria-label", messages.datepickerChangeView(monthTitle.textContent ?? ""));
        // No name written here. The grid is named by referring to the month in the header, and a written
        // label beside that reference loses to it in the name computation every time. ADR 0212.
        // Paging belongs to the day view: in the other two the arrows would move a month nobody is
        // looking at.
        prevButton.hidden = state.viewMode !== "days";
        nextButton.hidden = state.viewMode !== "days";
        grid.hidden = state.viewMode !== "days";
        renderPeriodView(state.viewMode, state.viewYear, state.viewMonth);
        // A closed calendar holds no cells. The popup element stays — it is built once and lives as long
        // as the field does — but what is inside it is a month's worth of buttons announced as a grid,
        // and a screen reader counting them in a picker nobody opened is being offered a control that
        // is not there. The month is rebuilt on the way back in, which is what happens on a month change
        // anyway.
        if (!state.open) {
            if (renderedMonth !== null) {
                grid.replaceChildren();
                cellEls = new Map();
                renderedYear = null;
                renderedMonth = null;
            }
        }
        else if (renderedYear !== state.viewYear || renderedMonth !== state.viewMonth) {
            // The controller owns which days exist and their state; the shared body owns the anatomy
            // (weekday header, one row per week) the themes lay out.
            cellEls = fillCalendar(grid, "datepicker", state.viewYear, state.viewMonth, dateLocale, (cell) => dispatch({ type: "select-date", iso: cell.iso }), widgetId);
            renderedYear = state.viewYear;
            renderedMonth = state.viewMonth;
        }
        // Paging moves the day under the header's buttons too, and the keyboard stays on the button that
        // paged: taken into the grid, the next press of Next would pick a day instead. A control the
        // render has hidden holds nothing, so the days take the keyboard from it. The render that brings
        // the days back takes it from the header too: the days are where a person returns to.
        const active = document.activeElement;
        const heldElsewhere = showedTheDays && active instanceof HTMLElement && popup.contains(active)
            && !grid.contains(active) && active.getClientRects().length > 0;
        showedTheDays = state.open && state.viewMode === "days";
        for (const cell of state.cells) {
            const button = cellEls.get(cell.iso);
            if (!button)
                continue;
            const part = view.parts[cell.iso];
            if (part)
                applyPart(button, part);
            button.disabled = cell.disabled;
            if (cell.focused && state.open && !heldElsewhere && document.activeElement !== button)
                button.focus();
        }
        /**
         * Anchored by the contract, like every other overlay — and **after the month is in it.**
         *
         * The placement, the size and the coordinates are `anchorOverlay`'s; this measures and applies
         * them. What it measures has to be the panel a person will see: positioned before the cells
         * exist, the calendar measures 54px against a content height of 276, so the policy is asked
         * whether a box a fifth of the real size fits below the field. It does, so the panel is placed
         * below and then drawn at its full height — clipped, where the policy answers "above" on the
         * same rect.
         *
         * And the wrong number does not correct itself: the measurement is held for the whole opening,
         * so nothing later in the panel's life re-asks. One pass, in the right order, is the fix.
         */
        if (state.open)
            positionOverlay(popup, shell.wrapper, anchoring);
        // The key a native submit reads this control's value under, after the parts are applied: the
        // shared control projection writes `name: null` for a field it was not given a name for, and a
        // part carrying `null` removes the attribute.
        applySubmissionNames(shell.root, "datepicker", f.name);
        // The value the field holds, carried by an input of its own. The control shows a date the way a
        // person writes one, and a name on it would send whichever spelling it is showing.
        syncSubmitValues(shell.root, f.name, state.selectedDate ? [state.selectedDate] : [], handle.disabled());
    });
    return withControls(() => {
        untrack();
        undismiss();
        unfocusout();
        effectRef.destroy();
        controller.destroy();
        shell.root.remove();
    }, 
    // Bounds move when a sibling field is answered — a return date that cannot precede a
    // departure — and the controller is told rather than the field remounted, which would forget
    // the month on screen.
    { setBounds: (min, max) => controller.setBounds(min, max) });
}
